<script>
import cloneDeep from 'lodash/cloneDeep'
import lazyLoadComponent from '@router/lazyload-component'

import Layout from '@layouts/main'
import { messageStore, messageDelete } from '@utils/message'

export default {
  page: {
    title: '編輯項目',
    meta: [{ name: 'description', content: 'Edit Product' }],
  },
  components: {
    Layout,
  },
  props: {
    defaultValue: {
      type: Object,
      required: true,
    },
    productType: {
      type: String,
      default: 'activity',
    },
  },
  data() {
    return {
      tryingToSave: false,
      saveButtonVisible: true,
      activeTab: '0',
      tabList: [
        {
          title: '基本資訊',
          component: () => lazyLoadComponent(import('./components/Step1BasicTab')),
          showFooter: true,
        },
        {
          title: '社群資料',
          component: () =>
            lazyLoadComponent(import('./components/Step9SocialInfo')),
          showFooter: true,
        },
        {
          title: '營業時間',
          component: () =>
            lazyLoadComponent(import('./components/Step6ServiceTime')),
          showFooter: true,
        },
        {
          title: '自訂題目',
          component: () =>
            lazyLoadComponent(import('./components/Step4ExtraField')),
          showFooter: true,
        },
        {
          title: '商店圖片',
          component: () =>
            lazyLoadComponent(import('./components/Step8StoreImg')),
          showFooter: true,
        },
      ],
      componentData: this.defaultValue,
      geocoder: '',
      map: [],
      hasGeo: false,
      labelName: '基本資訊',
      tempRef: {}
    }
  },
  computed: {
    productId() {
      return this.$route.params.productId
    },
  },
  mounted() {
    this.initGeocoder()
  },
  methods: {
    handleFormValidate(valid) {
      this.saveButtonVisible = valid
    },
    handleStorePrice(storeData) {
      const formatTime = (date) => {
        return date.match(/(\d+):(\d+):(00)$/g) ? date.replace(/:(00)$/g, '') : date
      }
      storeData.forEach((data) => {
        if (data.saleStartDate)
          data.saleStartDate = formatTime(data.saleStartDate)
        if (data.saleEndDate)
          data.saleEndDate = formatTime(data.saleEndDate)
      })
      return this.$store.dispatch('product/storePrices', {
        productId: this.productId,
        storeData,
      })
    },
    async handleStoreProduct(submitData) {
      const ordAddress = this.defaultValue.location
      const newAddress = this.tempRef.step1.submitData.location
      const isSame = `${ordAddress.city}${ordAddress.district}${ordAddress.address}` === `${newAddress.city}${newAddress.district}${newAddress.address}`
      if (!isSame) {
        await this.geocode(submitData)
        submitData.lat = this.map[0]
        submitData.lng = this.map[1]
      }
      submitData.type = this.productType
      return this.$store.dispatch('product/store', submitData)
    },
    handleStoreExtraField(submitData) {
      const extraFields = [...submitData.byOrder, ...submitData.bySize]
      const storeData = {
        productId: this.productId,
      }
      if (extraFields.length > 0) {
        storeData.extraFields = extraFields
      }
      if (extraFields.length === 0) {
        storeData.extraFields = []
      }
      return this.$store.dispatch('product/updateExtraField', storeData)
    },
    async getProductInfo() {
      await this.$store
        .dispatch('product/fetchSingle', this.productId)
        .then((product) => {
          this.componentData = cloneDeep(product)
        })
    },
    async handleSaveSuccess() {
      await this.$alert('儲存成功', '太棒了！', {
        confirmButtonText: '確認',
        type: 'success',
        center: true,
      })
      this.tryingToSave = false
    },
    /**
     * @description 如果使用者沒點開某個頁籤，代表他也沒更新，所以要刪掉某些key值，不要打到後端
     */
    updateKey(submitResult) {
      if (!this.tempRef.step3) {
        for (let i in this.componentData.serviceTimeSetting) {
          this.$set(this.componentData.serviceTimeSetting[i], 'timeRange',
            [this.componentData.serviceTimeSetting[i].start, this.componentData.serviceTimeSetting[i].end])
          // 如果營業時間一開始沒有isLastOrderCrossDate => 要塞假的進去
          if(!this.componentData.serviceTimeSetting[i].hasOwnProperty('isLastOrderCrossDate')){
            this.$set(this.componentData.serviceTimeSetting[i], 'isLastOrderCrossDate', 0)
          }
        }
        submitResult.serviceTimeSetting = this.componentData.serviceTimeSetting
      }
      return submitResult
    },
    async onClickSave(mode) {
      this.tryingToSave = true
      this.setTempRef()
      this.setFirstImageDefault()
      let step1Ref = this.tempRef.step1 || {}
      let step2Ref = this.tempRef.step2 || {}
      let step3Ref = this.tempRef.step3 || {}
      let step5Ref = this.tempRef.step5 || {}
      let timeData = step3Ref.serviceTimeSetting || {}
      for (let i in timeData) {
        timeData[i].start = timeData[i].timeRange[0]
        timeData[i].end = timeData[i].timeRange[1]
      }
      let submitResult = this.updateKey({
        ...step1Ref.formatSubmitData, // 1. 基本資訊資料
        ...step2Ref.submitData, // 2. 社群資料
        serviceTimeSetting: timeData, // 3. 營業時間
        // 4. 自訂題目有自己的API
        ...step5Ref.submitData        // 5. 商店圖片submitData
      })
      try {
        switch (this.activeTab) {
          case '0':
            // await step1Ref.geocode()
            await step1Ref.validate()
            await this.handleStoreProduct(submitResult)
            if (submitResult.id) await this.tempRef.step1.loadPage(submitResult.id)
            break
          case '2':
            await step3Ref.validate()
            if (!step3Ref.validateBySelf()) {
              return
            }
            await this.handleStoreProduct(submitResult)
            break
          case '1':
            await this.handleStoreProduct(submitResult)
            break
          case '4':
            await this.handleStoreProduct(submitResult)
            break
        }
        if (this.tempRef.step4 && this.tempRef.step4.list) {
          const list = this.tempRef.step4.list
          await this.handleStoreExtraField(list)
          if (this.activeTab === '3') await this.handleStoreProduct(submitResult)
          await this.getProductInfo()
        }
        messageStore('edit')
        if (mode === 'leave') {
          this.$router.push({ name: 'products' })
        }
      } catch (error) {
        // console.log(error)

      } finally {
        this.tryingToSave = false
      }
    },
    setFirstImageDefault() {
      try {
        if (this.tempRef.step5) {
          const { images } = this.tempRef.step5.submitData
          images.forEach((item, index) => {
            item.default = index === 0 ? 1 : 0
          })
        }
      } catch (error) {
        console.error(error.message)
      }
    },
    onClickLeave() {
      this.$router.push({ name: 'products' })
    },
    async beforeTabSwitch(activeName, oldActiveName) {
      if (oldActiveName === '0') {
        await this.tempRef.step1.validate()
      }
      if (oldActiveName === '2') {
        await this.tempRef.step3.validate()
      }
    },
    initGeocoder() {
      this.geocoder = new google.maps.Geocoder();
    },
    async geocode(submitData) {
      // const address = this.restaurant.address;
      const callback = await this.geocoder.geocode(
        // GeocoderRequest 物件: 帶入要轉換的地址與相關設定
        {
          address: `${submitData.location.city}${submitData.location.district}${submitData.location.address}`,
          // 限制轉換的結果必須是在台灣的範圍
          componentRestrictions: {
            country: "TW"
          }
        }
      );
      const lat = callback.results[0].geometry.location.lat()
      const lng = callback.results[0].geometry.location.lng()
      this.map = [lat, lng]
    },
    tabClick(tab, event) {
      this.setTempRef()
      this.labelName = tab.label
      const index = this.tabList.findIndex(item => item.title === tab.label)
      this.activeTab = `${index}`
    },
    setTempRef() {
      const refs = Object.entries(this.$refs)
      const existVueComponent = refs.find(item => item[1])
      if(existVueComponent[0] === 'step1'){
        // existVueComponent[1].$refs.step1Basic 代表使用者在當前基本資訊頁面
        // existVueComponent[1].tempRef 代表使用者點到其他頁面了
        existVueComponent[1] = existVueComponent[1].$refs.step1Basic || existVueComponent[1].tempRef
      }
      this.tempRef[`${existVueComponent[0]}`] = existVueComponent[1]
    }
  },
}
</script>

<template>
  <Layout :class="$style.page">
    <el-tabs v-model="activeTab" type="border-card" :before-leave="beforeTabSwitch" stretch class="isStyleBase"
      @tab-click="tabClick">
      <el-tab-pane v-for="(tab, index) in tabList" :key="index" :label="tab.title" :name="`${index}`">
      </el-tab-pane>
    </el-tabs>
    <div v-if="(0 !== +activeTab)" style="display:inline-block;" @click="onClickLeave" :class="$style.moreMargin">
      <el-page-header content="編輯分店"></el-page-header>
    </div>
    <keep-alive>
      <component :is="tabList[+activeTab].component" v-if="(0 === +activeTab)" ref="step1"
        :class="[$style['card-wrapper'], $style.moreMarginNoLeftRight, $style.topNoMargin]" :item-type="productType"
        :default-value="componentData">
        <div style="display:inline-block;" @click="onClickLeave" :class="$style.moreMargin">
          <el-page-header content="編輯分店"></el-page-header>
        </div>
      </component>
      <component :is="tabList[+activeTab].component" v-if="(1 === +activeTab)" ref="step2"
        :class="[$style['card-wrapper'], $style.moreMargin, $style.topNoMargin]" :default-value="componentData">
      </component>
      <component :is="tabList[+activeTab].component" v-if="(2 === +activeTab)" ref="step3"
        :class="[$style['card-wrapper'], $style.moreMargin, $style.topNoMargin]" :item-type="productType"
        :default-value="componentData"></component>
      <component :is="tabList[+activeTab].component" v-if="(3 === +activeTab)" ref="step4"
        :class="[$style['card-wrapper'], $style.moreMargin, $style.topNoMargin]" :default-value="componentData"
        @validate="handleFormValidate"></component>
      <component :is="tabList[+activeTab].component" v-if="(4 === +activeTab)" ref="step5"
        :class="[$style['card-wrapper'], $style.moreMargin, $style.topNoMargin]" :default-value="componentData">
      </component>
    </keep-alive>

    <div v-show="true" :class="$style.footer">
      <el-button class="is-round" @click="onClickLeave">取消</el-button>
      <el-button v-show="saveButtonVisible" class="is-round" :loading="tryingToSave" @click="onClickSave('leave')">
        儲存並返回列表</el-button>
      <el-button v-show="saveButtonVisible" class="is-round" type="primary" :loading="tryingToSave"
        @click="onClickSave">儲存變更</el-button>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@src/design/layout_detail_steps.scss';

.page {
  :global {
    .el-page-header {
      display: flex;
      align-items: center;
    }

    .el-page-header__left {
      background-color: white;
      width: 120px;
      height: 44px;
      padding: 12px 18px;
      border-radius: $border-radius;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid $color-border-base;
    }

    .el-page-header__content {
      font-size: 22px;
      color: $color-secondary;
    }
  }
}



.moreMargin {
  margin: 20px;

  &.topNoMargin {
    margin-top: 0;
    padding-top: 20px;
    padding-bottom: 80px;
  }
}
</style>
